/**
 * Standings + tiebreakers.
 *
 * Pure functions over a flat list of finished games, so the ordering rules from
 * the rulebook live in exactly one place and can be unit tested without a DB.
 *
 * Every competition breaks ties on overall record first. What comes after that
 * differs, and applying one chain to all of them rewrites history — Rev 2.0's
 * head-to-head criterion puts Carter above Ian in Season 1, which is not how
 * Season 1 was actually decided. So the chain is chosen per competition; see
 * TIEBREAKER_SETS below.
 */
const pct = (w, l, t) => {
    const g = w + l + t;
    return g === 0 ? 0 : (w + t * 0.5) / g;
};
/**
 * @param games   finished games only, in chronological order (streaks depend on it)
 * @param entrants everyone who should appear, including managers with no games yet
 */
export function computeStandings(games, entrants, tiebreakerSet = "rev2") {
    const acc = new Map();
    for (const e of entrants) {
        acc.set(e.managerId, {
            entrant: e,
            wins: 0,
            losses: 0,
            ties: 0,
            pf: 0,
            pa: 0,
            divWins: 0,
            divLosses: 0,
            divTies: 0,
            homeWins: 0,
            homeLosses: 0,
            awayWins: 0,
            awayLosses: 0,
            results: [],
        });
    }
    for (const g of games) {
        const home = acc.get(g.homeManagerId);
        const away = acc.get(g.awayManagerId);
        if (!home || !away)
            continue;
        home.pf += g.homeScore;
        home.pa += g.awayScore;
        away.pf += g.awayScore;
        away.pa += g.homeScore;
        const outcome = g.homeScore > g.awayScore ? "H" : g.awayScore > g.homeScore ? "A" : "T";
        if (outcome === "T") {
            home.ties++;
            away.ties++;
            if (g.divisional) {
                home.divTies++;
                away.divTies++;
            }
            home.results.push({ opponentId: g.awayManagerId, outcome: "T" });
            away.results.push({ opponentId: g.homeManagerId, outcome: "T" });
        }
        else {
            const winner = outcome === "H" ? home : away;
            const loser = outcome === "H" ? away : home;
            winner.wins++;
            loser.losses++;
            if (g.divisional) {
                winner.divWins++;
                loser.divLosses++;
            }
            if (outcome === "H") {
                home.homeWins++;
                away.awayLosses++;
            }
            else {
                away.awayWins++;
                home.homeLosses++;
            }
            home.results.push({
                opponentId: g.awayManagerId,
                outcome: outcome === "H" ? "W" : "L",
            });
            away.results.push({
                opponentId: g.homeManagerId,
                outcome: outcome === "A" ? "W" : "L",
            });
        }
    }
    // Base win percentage for every manager, needed before SOV/OWP can be computed.
    const winPctById = new Map();
    for (const [id, a] of acc)
        winPctById.set(id, pct(a.wins, a.losses, a.ties));
    const rows = [];
    for (const [id, a] of acc) {
        const beaten = a.results.filter((r) => r.outcome === "W");
        const sov = beaten.length === 0
            ? 0
            : beaten.reduce((s, r) => s + (winPctById.get(r.opponentId) ?? 0), 0) /
                beaten.length;
        const owp = a.results.length === 0
            ? 0
            : a.results.reduce((s, r) => s + (winPctById.get(r.opponentId) ?? 0), 0) /
                a.results.length;
        rows.push({
            managerId: id,
            name: a.entrant.name,
            divisionId: a.entrant.divisionId,
            divisionName: a.entrant.divisionName,
            divisionSortOrder: a.entrant.divisionSortOrder ?? null,
            wins: a.wins,
            losses: a.losses,
            ties: a.ties,
            gamesPlayed: a.wins + a.losses + a.ties,
            winPct: winPctById.get(id) ?? 0,
            pointsFor: a.pf,
            pointsAgainst: a.pa,
            diff: a.pf - a.pa,
            divWins: a.divWins,
            divLosses: a.divLosses,
            divTies: a.divTies,
            homeWins: a.homeWins,
            homeLosses: a.homeLosses,
            awayWins: a.awayWins,
            awayLosses: a.awayLosses,
            strengthOfVictory: sov,
            opponentWinPct: owp,
            streak: computeStreak(a.results),
            rank: 0,
            tiebreakerNote: null,
        });
    }
    return rankRows(rows, acc, tiebreakerSet);
}
function computeStreak(results) {
    if (results.length === 0)
        return "—";
    const last = results[results.length - 1].outcome;
    let count = 0;
    for (let idx = results.length - 1; idx >= 0; idx--) {
        if (results[idx].outcome !== last)
            break;
        count++;
    }
    return `${last}${count}`;
}
/**
 * Head-to-head between two managers: their record against each other.
 * With more than two tied managers this becomes "record among tied teams",
 * which is the same computation restricted to the tied group.
 */
function recordAmong(a, group) {
    const rel = a.results.filter((r) => group.has(r.opponentId));
    if (rel.length === 0)
        return -1; // never played them: cannot separate on this
    const w = rel.filter((r) => r.outcome === "W").length;
    const l = rel.filter((r) => r.outcome === "L").length;
    const t = rel.filter((r) => r.outcome === "T").length;
    return pct(w, l, t);
}
const HEAD_TO_HEAD = {
    label: "head-to-head",
    key: (_r, a, group) => recordAmong(a, group),
};
const DIVISION_RECORD = {
    label: "division record",
    key: (r) => pct(r.divWins, r.divLosses, r.divTies),
};
const STRENGTH_OF_VICTORY = {
    label: "strength of victory",
    key: (r) => r.strengthOfVictory,
};
const OPPONENT_WIN_PCT = {
    label: "opponent win %",
    key: (r) => r.opponentWinPct,
};
const POINT_DIFFERENTIAL = {
    label: "point differential",
    key: (r) => r.diff,
};
/** The last resort is a random draw, which a website must not actually perform. */
const RANDOM_DRAW = "random draw";
/**
 * Which chain applies to which competition.
 *
 * - `rev2`  Season 2 onward, per Rules/Information Rev 2.0.
 * - `rev1`  Season 1, which ran on Rev 1.0 and separated on point differential.
 * - `roundRobin`  The Christmas Tournament: everyone plays everyone once, so
 *   head-to-head always resolves and nothing else is needed.
 */
export const TIEBREAKER_SETS = {
    rev2: [HEAD_TO_HEAD, DIVISION_RECORD, STRENGTH_OF_VICTORY, OPPONENT_WIN_PCT],
    rev1: [POINT_DIFFERENTIAL],
    roundRobin: [HEAD_TO_HEAD],
};
/**
 * Pick the chain for a competition. Rules revision is authoritative where it's
 * recorded; a tournament without one is a round robin.
 */
export function tiebreakerSetFor(season) {
    if (season.rulesRevision?.startsWith("1."))
        return "rev1";
    if (season.kind === "tournament")
        return "roundRobin";
    return "rev2";
}
/** Human-readable chain, for the "how ties are broken" copy on the standings page. */
export function tiebreakerLabels(setId) {
    return [
        "overall record",
        ...TIEBREAKER_SETS[setId].map((c) => c.label),
        RANDOM_DRAW,
    ];
}
function rankRows(rows, acc, setId) {
    const criteria = TIEBREAKER_SETS[setId];
    // Group by identical overall record, then break ties within each group.
    const sorted = [...rows].sort((x, y) => y.winPct - x.winPct);
    const out = [];
    let idx = 0;
    while (idx < sorted.length) {
        let end = idx;
        while (end + 1 < sorted.length && sorted[end + 1].winPct === sorted[idx].winPct) {
            end++;
        }
        const tied = sorted.slice(idx, end + 1);
        if (tied.length === 1) {
            out.push(tied[0]);
        }
        else {
            const group = new Set(tied.map((r) => r.managerId));
            const scored = tied.map((r) => {
                const a = acc.get(r.managerId);
                return { row: r, keys: criteria.map((c) => c.key(r, a, group)) };
            });
            scored.sort((x, y) => {
                for (let k = 0; k < x.keys.length; k++) {
                    if (x.keys[k] !== y.keys[k])
                        return y.keys[k] - x.keys[k];
                }
                // Stable alphabetical stand-in for the random draw.
                return x.row.name.localeCompare(y.row.name);
            });
            // Before anyone has played, everybody is level at 0-0 and every row would
            // be annotated "random draw". True, and completely useless.
            const anyGamesPlayed = tied.some((r) => r.gamesPlayed > 0);
            scored.forEach((s, pos) => {
                // The note belongs to the manager who won the tiebreak, naming who they
                // edged out — so annotate against the row *below*, not the one above.
                const below = scored[pos + 1];
                if (below && anyGamesPlayed) {
                    let criterion = RANDOM_DRAW;
                    for (let k = 0; k < s.keys.length; k++) {
                        if (s.keys[k] !== below.keys[k]) {
                            criterion = criteria[k].label;
                            break;
                        }
                    }
                    s.row.tiebreakerNote = {
                        criterion,
                        overManagerId: below.row.managerId,
                        overName: below.row.name,
                    };
                }
                out.push(s.row);
            });
        }
        idx = end + 1;
    }
    out.forEach((r, n) => {
        r.rank = n + 1;
    });
    return out;
}
/** Split a ranked league table into per-division tables, preserving order. */
export function byDivision(rows) {
    const groups = new Map();
    for (const r of rows) {
        const key = String(r.divisionId ?? "none");
        if (!groups.has(key))
            groups.set(key, []);
        groups.get(key).push(r);
    }
    return [...groups.values()]
        .map((rs) => ({
        divisionId: rs[0].divisionId,
        divisionName: rs[0].divisionName,
        sortOrder: rs[0].divisionSortOrder,
        rows: rs.map((r, n) => ({ ...r, rank: n + 1 })),
    }))
        .sort((a, b) => (a.sortOrder ?? Number.MAX_SAFE_INTEGER) -
        (b.sortOrder ?? Number.MAX_SAFE_INTEGER) ||
        (a.divisionName ?? "").localeCompare(b.divisionName ?? ""));
}
/**
 * Playoff seeding per Rev 2.0: the two division winners take seeds 1 and 2
 * (ordered between themselves by the same tiebreakers), the rest are wildcards
 * ordered by the league table.
 */
export function playoffSeeds(rows, seedCount) {
    const divisions = byDivision(rows);
    if (divisions.length < 2)
        return rows.slice(0, seedCount);
    // byDivision returns copies, so identity lookups against `rows` never match.
    // Order the division winners by their position in the league table, by id.
    const leaguePosition = new Map(rows.map((r, n) => [r.managerId, n]));
    const winners = divisions
        .map((d) => d.rows[0])
        .filter(Boolean)
        .sort((a, b) => (leaguePosition.get(a.managerId) ?? Number.MAX_SAFE_INTEGER) -
        (leaguePosition.get(b.managerId) ?? Number.MAX_SAFE_INTEGER));
    const winnerIds = new Set(winners.map((w) => w.managerId));
    const wildcards = rows.filter((r) => !winnerIds.has(r.managerId));
    return [...winners, ...wildcards].slice(0, seedCount);
}
//# sourceMappingURL=standings.js.map